import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { ObjectMenu, ObjectMenuButton } from "./ObjectMenu";
import { useObjectList } from "./useObjectList";
import { isMirrorMedia } from "../../utils/object-menu-mirror-utils";
import { useSidebarContext } from "../SidebarContext";
import { ArtworkSidebar } from "./ArtworkSidebar";
import { PlaceartSidebar } from "./PlaceArtSidebar";
import {
  usePinObject,
  useRemoveObject,
  useGoToSelectedObject,
  useMintObject,
  useArtworkObject,
  getObjectUrl,
  isPlayer,
  isMe,
  useHideAvatar,
  usePlaceartEdit,
  useLockedObject
} from "./object-hooks";
import { ReactComponent as PinIcon } from "../icons/Pin.svg";
import { ReactComponent as LinkIcon } from "../icons/Link.svg";
import { ReactComponent as ViewIcon } from "../icons/View.svg";
import { ReactComponent as DeleteIcon } from "../icons/Delete.svg";
import { ReactComponent as AvatarIcon } from "../icons/Avatar.svg";
import { ReactComponent as HideIcon } from "../icons/Hide.svg";
import { ReactComponent as MetamaskIcon } from "../icons/metamask.svg";
import { ReactComponent as InfoIcon } from "../icons/Info2.svg";
import { ReactComponent as PenIcon } from "../icons/Pen.svg";
import { ReactComponent as ArtworkIcon } from "../icons/ArtworkIcon.svg";
import { FormattedMessage } from "react-intl";
import { ArtworkFormUploadContainer } from "./ArtworkFormUploadContainer";
import {
  buildDeviceLinkHandoffPayload,
  encryptDeviceLinkPayload,
  getDeviceLinkRoomPath
} from "../../utils/device-link-crypto";
import { formatDeviceLinkCode } from "../../utils/device-link-code";
import { isDeviceLinkGrantInactive } from "../../utils/device-link-grant-status";

function MyMenuItems({ onOpenProfile }) {
  return (
    <ObjectMenuButton onClick={onOpenProfile}>
      <AvatarIcon />
      <span>
        <FormattedMessage id="object-menu.edit-avatar-button" defaultMessage="Edit Avatar" />
      </span>
    </ObjectMenuButton>
  );
}

MyMenuItems.propTypes = {
  onOpenProfile: PropTypes.func.isRequired
};

function PlayerMenuItems({ hubChannel, activeObject, deselectObject }) {
  const hideAvatar = useHideAvatar(hubChannel, activeObject.el);

  return (
    <ObjectMenuButton
      onClick={() => {
        deselectObject();
        hideAvatar();
      }}
    >
      <HideIcon />
      <span>
        <FormattedMessage id="object-menu.hide-avatar-button" defaultMessage="Hide" />
      </span>
    </ObjectMenuButton>
  );
}

PlayerMenuItems.propTypes = {
  hubChannel: PropTypes.object.isRequired,
  activeObject: PropTypes.object.isRequired,
  deselectObject: PropTypes.func.isRequired
};

function ObjectMenuItems({
  hubChannel,
  scene,
  activeObject,
  deselectObject,
  onGoToObject,
  onTogglesidebar,
  togglePlaceart,
  toggleArtworkModal
}) {
  const { canPin, isPinned, togglePinned } = usePinObject(hubChannel, scene, activeObject, deselectObject);
  const { canRemoveObject, removeObject } = useRemoveObject(hubChannel, scene, activeObject);
  const { canMintObject, mintObject, isMintingObject } = useMintObject(activeObject);
  const { isLocked, canLockObjects } = useLockedObject(hubChannel, scene, activeObject);

  const { canEditArtwork, canBeAnArtwork, artworkReady } = usePlaceartEdit(hubChannel, scene, activeObject);
  const { canGoTo, goToSelectedObject } = useGoToSelectedObject(scene, activeObject);
  const url = getObjectUrl(activeObject);

  const isArtwork =
    activeObject?.el?.components["gltf-model-plus"]?.model?.userData?.metadata?.isArtwork ||
    activeObject?.el?.components["artwork"];

  const artworkCanBeEdited =
    isArtwork &&
    activeObject?.el?.components["artwork"]?.data?.image &&
    canEditArtwork &&
    !activeObject?.el?.components["artwork"]?.hasTransparency;

  return (
    <>
      <ObjectMenuButton
        disabled={!canPin || isMirrorMedia(activeObject.el.parentEl) || (isArtwork && !artworkReady) || !canLockObjects}
        onClick={togglePinned}
      >
        <PinIcon width={24} height={24} />
        <span>
          {isPinned || isLocked ? (
            <FormattedMessage id="object-menu.unpin-object-button" defaultMessage="Unpin" />
          ) : (
            <FormattedMessage id="object-menu.pin-object-button" defaultMessage="Pin" />
          )}
        </span>
      </ObjectMenuButton>
      {canBeAnArtwork && (
        <ObjectMenuButton
          onClick={() => {
            toggleArtworkModal();
          }}
        >
          <ArtworkIcon width={24} height={24} />
          <span>
            <FormattedMessage id="object-menu.convert-artwork-object-button" defaultMessage="Artwork" />
          </span>
        </ObjectMenuButton>
      )}
      {url && (
        <ObjectMenuButton as="a" href={url} target="_blank" rel="noopener noreferrer">
          <LinkIcon width={24} height={24} />
          <span>
            <FormattedMessage id="object-menu.object-link-button" defaultMessage="Link" />
          </span>
        </ObjectMenuButton>
      )}
      {isArtwork && (
        <ObjectMenuButton
          onClick={() => {
            onTogglesidebar();
          }}
        >
          <InfoIcon width={24} height={24} />
          <span>
            <FormattedMessage id="object-menu.info-artwork-object-button" defaultMessage="Info" />
          </span>
        </ObjectMenuButton>
      )}
      {artworkCanBeEdited && (
        <ObjectMenuButton
          onClick={() => {
            togglePlaceart();
          }}
        >
          <PenIcon width={24} height={24} />
          <span>
            <FormattedMessage id="object-menu.edit-artwork-object-button" defaultMessage="Edit" />
          </span>
        </ObjectMenuButton>
      )}
      <ObjectMenuButton
        disabled={!canGoTo}
        onClick={() => {
          goToSelectedObject();
          deselectObject();
          onGoToObject();
        }}
      >
        <ViewIcon width={24} height={24} />
        <span>
          <FormattedMessage id="object-menu.view-object-button" defaultMessage="View" />
        </span>
      </ObjectMenuButton>
      {url && canMintObject && (
        <ObjectMenuButton
          disabled={isMintingObject.current}
          onClick={() => {
            mintObject();
          }}
        >
          <MetamaskIcon width={24} height={24} />
          <span>
            <FormattedMessage id="object-menu.mint-object-button" defaultMessage="Mint NFT" />
          </span>
        </ObjectMenuButton>
      )}
      <ObjectMenuButton
        disabled={!canRemoveObject}
        onClick={() => {
          removeObject();
          deselectObject();
        }}
      >
        <DeleteIcon width={24} height={24} />
        <span>
          <FormattedMessage id="object-menu.delete-object-button" defaultMessage="Delete" />
        </span>
      </ObjectMenuButton>
    </>
  );
}

ObjectMenuItems.propTypes = {
  hubChannel: PropTypes.object.isRequired,
  scene: PropTypes.object.isRequired,
  activeObject: PropTypes.object.isRequired,
  deselectObject: PropTypes.func.isRequired,
  onGoToObject: PropTypes.func.isRequired,
  onTogglesidebar: PropTypes.func.isRequired,
  togglePlaceart: PropTypes.func.isRequired,
  toggleArtworkModal: PropTypes.func.isRequired
};

export function ObjectMenuContainer({
  hubChannel,
  scene,
  onOpenProfile,
  onGoToObject,
  dashboardChannel,
  enterSceneInVR,
  onDeviceLinkHandoffConfirmed
}) {
  const { objects, activeObject, deselectObject, selectNextObject, selectPrevObject, toggleLights, lightsEnabled } =
    useObjectList();
  const { sidebarOpen, toggleSidebar, closeSidebar } = useArtworkObject(hubChannel, scene, activeObject);

  const { placeartOpen, closePlaceart, togglePlaceart, artworkModalOpen, toggleArtworkModal } = usePlaceartEdit(
    hubChannel,
    scene,
    activeObject
  );

  const [artworkOpen, setArtworkOpen] = useState(false);
  useEffect(() => {
    setArtworkOpen(artworkModalOpen);
  }, [artworkModalOpen]);

  // Check if inspect mode is from external loader
  const cameraSystem = scene?.systems?.["hubs-systems"]?.cameraSystem;
  const hideNotch = cameraSystem?.inspectFromExternalLoader || false;
  const isFromExternalLoader = cameraSystem?.inspectFromExternalLoader || false;

  // Check if active object is an artwork AND loaded via external loader
  const hasArtwork = activeObject?.el?.components?.["artwork"];
  const showArtworkInfoButton = !!hasArtwork && isFromExternalLoader;

  // Get sidebar state from context
  const { sidebarId } = useSidebarContext();
  const isArtworkInfoOpen = sidebarId === "artwork-info";

  // Handle opening artwork info sidebar
  const handleOpenArtworkInfo = () => {
    if (hasArtwork) {
      window.dispatchEvent(new CustomEvent("open-artwork-info", { detail: activeObject.el }));
    }
  };

  // VR popover state - only show when from external loader
  const [enterOnVROpen, setEnterOnVROpen] = useState(false);
  const [linkCode, setLinkCode] = useState(null);
  const [linkCodeCancel, setLinkCodeCancel] = useState(null);
  const [loadingLinkCode, setLoadingLinkCode] = useState(false);
  const activeDeviceLinkRef = useRef(null);
  const cancelDeviceLinkRequestedRef = useRef(false);

  const clearDeviceLink = () => {
    activeDeviceLinkRef.current = null;
    setLinkCode(null);
    setLinkCodeCancel(null);
    setLoadingLinkCode(false);
  };

  const attemptLink = async () => {
    if (!dashboardChannel?.createDeviceLink) return;

    cancelDeviceLinkRequestedRef.current = false;
    activeDeviceLinkRef.current = null;
    setLoadingLinkCode(true);

    try {
      const roomPath = getDeviceLinkRoomPath();
      const deviceLink = await dashboardChannel?.createDeviceLink({ roomPath });
      if (!deviceLink?.grant_id || !deviceLink?.user_code) {
        clearDeviceLink();
        return;
      }

      const sourceSecret = deviceLink.source_secret || null;
      if (cancelDeviceLinkRequestedRef.current) {
        dashboardChannel?.cancelDeviceLink?.(deviceLink.grant_id, { sourceSecret });
        clearDeviceLink();
        return;
      }

      const displayCode = formatDeviceLinkCode(deviceLink.user_code);
      const activeDeviceLink = { grantId: deviceLink.grant_id, sourceSecret };

      activeDeviceLinkRef.current = activeDeviceLink;
      setLinkCode(displayCode);
      setLinkCodeCancel(() => () => dashboardChannel?.cancelDeviceLink?.(deviceLink.grant_id, { sourceSecret }));
      setLoadingLinkCode(false);

      while (activeDeviceLinkRef.current === activeDeviceLink) {
        const grant = await dashboardChannel?.getDeviceLink(deviceLink.grant_id, {
          sourceSecret: activeDeviceLink.sourceSecret
        });
        const claim = grant?.claim;

        if (claim?.claim_id && claim?.target_encryption_public_jwk) {
          const handoffPayload = buildDeviceLinkHandoffPayload(window.APP.store, roomPath);
          const encryptedHandoffPayload = await encryptDeviceLinkPayload(
            claim.target_encryption_public_jwk,
            handoffPayload
          );
          await dashboardChannel?.confirmDeviceLink({
            grantId: deviceLink.grant_id,
            claimId: claim.claim_id,
            encryptedHandoffPayload,
            sourceSecret: activeDeviceLink.sourceSecret
          });
          clearDeviceLink();
          await onDeviceLinkHandoffConfirmed?.();
          break;
        }

        if (isDeviceLinkGrantInactive(grant)) {
          clearDeviceLink();
          break;
        }

        await new Promise(resolve => setTimeout(resolve, 1500));
      }
    } catch (error) {
      clearDeviceLink();
      console.error(error);
    }
  };

  const handleEnterOnVROpen = () => {
    // Always show the popover and generate link code
    cancelDeviceLinkRequestedRef.current = false;
    attemptLink();
    setEnterOnVROpen(true);
  };

  const handleEnterOnVRClose = () => {
    cancelDeviceLinkRequestedRef.current = true;
    if (linkCodeCancel) {
      try {
        linkCodeCancel();
      } catch (e) {
        console.error(e);
      }
    }
    clearDeviceLink();
    setEnterOnVROpen(false);
  };

  const handleEnterOnConnectedHeadset = async () => {
    if (enterSceneInVR && typeof enterSceneInVR === "function") {
      enterSceneInVR();
    }
    cancelDeviceLinkRequestedRef.current = true;
    if (linkCodeCancel) {
      linkCodeCancel();
    }
    clearDeviceLink();
    setEnterOnVROpen(false);
  };

  let menuItems;

  if (isMe(activeObject)) {
    menuItems = <MyMenuItems onOpenProfile={onOpenProfile} />;
  } else if (isPlayer(activeObject)) {
    menuItems = <PlayerMenuItems hubChannel={hubChannel} activeObject={activeObject} deselectObject={deselectObject} />;
  } else {
    menuItems = (
      <ObjectMenuItems
        hubChannel={hubChannel}
        scene={scene}
        activeObject={activeObject}
        deselectObject={deselectObject}
        onGoToObject={onGoToObject}
        onTogglesidebar={toggleSidebar}
        togglePlaceart={togglePlaceart}
        toggleArtworkModal={toggleArtworkModal}
      />
    );
  }

  return (
    <>
      <ObjectMenu
        title={<FormattedMessage id="object-menu.title" defaultMessage="Object" />}
        currentObjectIndex={objects.indexOf(activeObject)}
        objectCount={objects.filter(object => !object.is2D).length}
        onClose={deselectObject}
        onBack={deselectObject}
        onNextObject={selectNextObject}
        onPrevObject={selectPrevObject}
        onToggleLights={toggleLights}
        lightsEnabled={lightsEnabled}
        hideNotch={hideNotch}
        onOpenArtworkInfo={handleOpenArtworkInfo}
        showArtworkInfoButton={showArtworkInfoButton}
        isArtworkInfoOpen={isArtworkInfoOpen}
        showEnterOnVR={isFromExternalLoader}
        enterOnVROpen={enterOnVROpen}
        onEnterOnVROpen={handleEnterOnVROpen}
        onEnterOnVRClose={handleEnterOnVRClose}
        linkCode={linkCode}
        loadingLinkCode={loadingLinkCode}
        onEnterOnConnectedHeadset={handleEnterOnConnectedHeadset}
        attemptLink={attemptLink}
      >
        {menuItems}
      </ObjectMenu>
      {isMe(activeObject) && !isPlayer(activeObject) && (
        <>
          <ArtworkSidebar close={() => closeSidebar()} open={sidebarOpen} />
          <PlaceartSidebar close={closePlaceart} open={placeartOpen}></PlaceartSidebar>
          <ArtworkFormUploadContainer
            artwork={activeObject}
            scene={scene}
            open={artworkOpen}
            onClose={toggleArtworkModal}
          ></ArtworkFormUploadContainer>
        </>
      )}
    </>
  );
}

ObjectMenuContainer.propTypes = {
  hubChannel: PropTypes.object.isRequired,
  scene: PropTypes.object.isRequired,
  onOpenProfile: PropTypes.func.isRequired,
  onGoToObject: PropTypes.func.isRequired,
  dashboardChannel: PropTypes.object,
  enterSceneInVR: PropTypes.func,
  onDeviceLinkHandoffConfirmed: PropTypes.func
};
